import type { GroundKind, GroundPiece } from '../../site/types';
import type { PaletteClassName, PaletteModel } from '../../data/types';
import type { LinearRGB } from '../../core/color';
import type { FieldClassesFile } from '../../data/generated/fieldClasses';
import { FIELD_ALBEDO_CLASSES, type FieldRegion, type FieldVotes } from '../../data/fieldClasses';
import type { VertexStyle } from '../mesh/meshBuilder';
import { SURFACE, edgeIdFor, type SurfaceKind } from '../mesh/attributes';
import { minAreaOBB } from '../../geo/polygon';

export type FieldRegionClasses = FieldClassesFile['regions'];

export const GROUND_KIND_TO_PALETTE: Readonly<Record<GroundKind, PaletteClassName>> = {
  meadow: 'nearFields',
  northShore: 'nearFields',
  polder: 'polderFields',
  field: 'polderFields',
  garden: 'gardenGrass',
  orchard: 'gardenGrass',
  cloister: 'gardenGrass',
  street: 'streetGround',
  square: 'streetGround',
  // The painting shows the ground behind the city's houses as bare yards: 1.0% of the city is
  // gardenGrass there, and trodden dike earth, without garden beds, measured nearest (look-2, round 9).
  city: 'dikeRoads',
  // de Fremery, De opkomst der Amsterdamse haven (1925) p.15: on the 1538 view the Lastage holds
  // kitchen gardens south of the Sint Anthonisdijk and reed land north of it, not bare ground.
  lastage: 'gardenGrass',
  // The painting's timber yard on the Lastage strip and spit is dark earth (Lab 28.7/-0.1/24.1),
  // nearest roofDark; its colour map has the rest (terrain-colour-2, 2026-09-28).
  yard: 'roofDark',
  dike: 'dikeRoads',
};

/* Kinds whose colour is the painted ground they are, not the field classes their pixels vote for:
   the few pixels a yard shows vote the grass the colour map leaves out of it. */
const KINDS_WITHOUT_VOTES: ReadonlySet<GroundKind> = new Set<GroundKind>(['yard']);

/* The Lastage is drawn as painted field, so the painted material lays the painter's own colour over
   it from the colour map (tools/fields): dark earth, timber yards and kitchen gardens where he put
   them, its kind colour standing where the map has none (terrain-colour-2, 2026-09-28). */
const GROUND_KIND_TO_SURFACE: Readonly<Record<GroundKind, SurfaceKind>> = {
  polder: SURFACE.field,
  field: SURFACE.field,
  garden: SURFACE.garden,
  orchard: SURFACE.garden,
  cloister: SURFACE.garden,
  street: SURFACE.street,
  square: SURFACE.street,
  meadow: SURFACE.ground,
  northShore: SURFACE.ground,
  city: SURFACE.ground,
  lastage: SURFACE.field,
  yard: SURFACE.field,
  dike: SURFACE.ground,
};

/* The mesh builder has written the seed nowhere since round 17, when aMeta.z began to carry the
   surface pattern's code (meshBuilder.ts), so every ground piece gives the same one. */
const GROUND_SEED = 0;

function isField(piece: GroundPiece): boolean {
  return piece.kind === 'polder' || piece.kind === 'field';
}

function isGarden(piece: GroundPiece): boolean {
  return piece.kind === 'garden' || piece.kind === 'orchard' || piece.kind === 'cloister';
}

function extentOf(piece: GroundPiece): readonly [number, number] {
  if (isField(piece)) return [piece.ditchSpacing, 1.2];
  // Fruit-tree ground is open soil/grass, not narrow kitchen-garden beds.
  if (piece.kind === 'orchard') return [0, 0];
  if (isGarden(piece)) return [1.5, 0.25];
  return [0, 0];
}

function strokeAngleOf(piece: GroundPiece): number {
  if (isField(piece)) return piece.ditchAngle;
  if (isGarden(piece)) {
    const obb = minAreaOBB(piece.polygon.outer);
    return Math.atan2(obb.axisU[1], obb.axisU[0]);
  }
  return 0;
}

export function regionIdOf(piece: GroundPiece): string {
  return piece.regionId;
}

// A field region takes the class the painting voted for it (tools/fields); every other piece, and
// every piece when no classes are given, takes its kind's class.
export function groundPaletteClass(
  piece: GroundPiece,
  fields: FieldRegionClasses
): PaletteClassName {
  if (KINDS_WITHOUT_VOTES.has(piece.kind)) return GROUND_KIND_TO_PALETTE[piece.kind];
  return fields[regionIdOf(piece)]?.class ?? GROUND_KIND_TO_PALETTE[piece.kind];
}

// A region that borrows its class shows its lender's votes as well.
function votesBehind(region: FieldRegion, fields: FieldRegionClasses): FieldVotes {
  const lender = region.borrowedFrom === undefined ? undefined : fields[region.borrowedFrom];
  return (lender ?? region).votes;
}

/* The painting shows each field as one even colour, not as strips of the classes its pixels voted
   for (strip-to-strip L* p90−p10 1.2–2.3 in the painter view, against 4.2–6.7 where the runner-up
   took whole strips, round 10), so a field region takes the mean of the candidate albedos weighted
   by their shares of its votes. Where nobody voted it keeps its class's albedo. */
function groundAlbedo(
  piece: GroundPiece,
  palette: PaletteModel,
  fields: FieldRegionClasses
): LinearRGB {
  const region = KINDS_WITHOUT_VOTES.has(piece.kind) ? undefined : fields[regionIdOf(piece)];
  if (!region) return palette[GROUND_KIND_TO_PALETTE[piece.kind]].median.linear;
  const votes = votesBehind(region, fields);
  const total = FIELD_ALBEDO_CLASSES.reduce((sum, cls) => sum + votes[cls], 0);
  if (total === 0) return palette[region.class].median.linear;
  const channel = (k: 0 | 1 | 2) =>
    FIELD_ALBEDO_CLASSES.reduce(
      (sum, cls) => sum + (votes[cls] / total) * palette[cls].median.linear[k],
      0
    );
  return [channel(0), channel(1), channel(2)];
}

export function groundStyle(
  piece: GroundPiece,
  palette: PaletteModel,
  fields: FieldRegionClasses
): VertexStyle {
  return {
    color: groundAlbedo(piece, palette, fields),
    surface: GROUND_KIND_TO_SURFACE[piece.kind],
    edgeId: edgeIdFor(piece.index),
    seed: GROUND_SEED,
    strokeAngle: strokeAngleOf(piece),
    extent: extentOf(piece),
  };
}
